import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Lock, LogOut, RefreshCw, Download, ChevronDown } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { api } from "@/lib/api";

const Row = ({ title, value }) => {
  if (value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0))
    return null;
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 py-1.5 text-sm">
      <span className="text-[#0B0A08]/45">{title}</span>
      <span className="text-[#0B0A08]/85">{Array.isArray(value) ? value.join(", ") : String(value)}</span>
    </div>
  );
};

const Card = ({ item, token, kind }) => {
  const [open, setOpen] = useState(false);
  const heading = kind === "family" ? item.contact_name : item.full_name;

  const openCv = async () => {
    try {
      const res = await api.get(`/admin/files/${item.cv_file.storage_path}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      /* download failed */
    }
  };
  return (
    <div className="rounded-xl border border-[#0B0A08]/12 bg-white" data-testid="submission-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 p-5 text-left"
      >
        <div>
          <p className="font-medium text-[#0B0A08]">{heading || "—"}</p>
          <p className="text-xs text-[#0B0A08]/50">
            {item.email} · {new Date(item.created_at).toLocaleString()}
          </p>
        </div>
        <ChevronDown className={`h-5 w-5 shrink-0 text-[#0B0A08]/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-[#0B0A08]/10 p-5 pt-3">
          {kind === "family" ? (
            <>
              <Row title="Arrangement" value={item.arrangement_type} />
              <Row title="Living" value={item.living} />
              <Row title="Emirate" value={item.emirate} />
              <Row title="City" value={item.city} />
              <Row title="Start date" value={item.start_date} />
              <Row title="Schedule" value={item.schedule} />
              <Row title="Children" value={item.number_of_children} />
              <Row title="Age ranges" value={item.age_ranges} />
              <Row title="Experience" value={item.experience_skills} />
              <Row title="Note" value={item.note} />
              <Row title="Phone" value={item.phone} />
              <Row title="Preferred" value={item.preferred_contact} />
              <Row title="WhatsApp OK" value={item.whatsapp_consent ? "Yes" : "No"} />
            </>
          ) : (
            <>
              <Row title="Emirate" value={item.emirate} />
              <Row title="Experience" value={item.years_experience} />
              <Row title="Age groups" value={item.age_groups} />
              <Row title="Care types" value={item.care_types} />
              <Row title="Languages" value={item.languages} />
              <Row title="Training" value={item.training_qualifications} />
              <Row title="First Aid/CPR" value={item.first_aid_cpr ? `Yes${item.first_aid_expiry ? ` (exp. ${item.first_aid_expiry})` : ""}` : "No"} />
              <Row title="References" value={item.references_available ? "Available" : "—"} />
              <Row title="Schedule" value={item.schedule} />
              <Row title="Intro" value={item.intro} />
              <Row title="Phone" value={item.phone} />
              {item.cv_file && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={openCv}
                    data-testid="cv-download-link"
                    className="inline-flex items-center gap-2 rounded-full bg-[#0B0A08] px-4 py-2 text-xs font-semibold text-[#F7F4ED] hover:bg-[#0B0A08]/85"
                  >
                    <Download className="h-3.5 w-3.5" /> {item.cv_file.original_filename}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default function Admin() {
  useSeo({ title: "Team Admin — Nanny Poppins UAE" });
  const [token, setToken] = useState(() => sessionStorage.getItem("np_admin_token") || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("family");

  const load = async (tk) => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/submissions", { headers: { Authorization: `Bearer ${tk}` } });
      setData(res.data);
      setAuthed(true);
      setToken(tk);
      sessionStorage.setItem("np_admin_token", tk);
    } catch (e) {
      if (e?.response?.status === 429) {
        setError("Too many attempts. Please wait a few minutes and try again.");
      } else {
        setError("Invalid token or unable to load submissions.");
      }
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line
  }, []);

  const logout = () => {
    sessionStorage.removeItem("np_admin_token");
    setToken("");
    setAuthed(false);
    setData(null);
    setInput("");
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0A08] px-4">
        <div className="w-full max-w-sm rounded-2xl border border-[#C6A04A]/25 bg-[#F7F4ED] p-8" data-testid="admin-login">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#C6A04A]/15 text-[#C6A04A]">
            <Lock className="h-6 w-6" />
          </div>
          <h1 className="mt-5 font-serif text-2xl tracking-tight text-[#0B0A08]">Team access</h1>
          <p className="mt-2 text-sm text-[#0B0A08]/60">Enter the shared access token to view submissions.</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              load(input.trim());
            }}
            className="mt-6 space-y-4"
          >
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Access token"
              data-testid="admin-token-input"
              className="h-11 w-full rounded-xl border border-[#0B0A08]/15 bg-white px-4 text-sm text-[#0B0A08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C6A04A]"
            />
            {error && <p className="text-xs font-medium text-red-600" data-testid="admin-error">{error}</p>}
            <button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="admin-login-button"
              className="h-11 w-full rounded-full bg-[#0B0A08] text-sm font-semibold text-[#F7F4ED] hover:bg-[#0B0A08]/85 disabled:opacity-60"
            >
              {loading ? "Checking…" : "Enter"}
            </button>
          </form>
          <Link to="/" className="mt-6 block text-center text-xs text-[#0B0A08]/45 hover:text-[#C6A04A]">
            Back to website
          </Link>
        </div>
      </div>
    );
  }

  const list = tab === "family" ? data?.family_requests || [] : data?.nanny_applications || [];

  return (
    <div className="min-h-screen bg-[#F7F4ED]">
      <header className="border-b border-[#0B0A08]/10 bg-[#0B0A08] text-[#F7F4ED]">
        <div className="container-page flex h-16 items-center justify-between">
          <span className="font-serif text-lg">Nanny Poppins · Team</span>
          <div className="flex items-center gap-2">
            <button onClick={() => load(token)} data-testid="admin-refresh" className="inline-flex items-center gap-2 rounded-full border border-[#F7F4ED]/25 px-4 py-2 text-xs font-semibold hover:border-[#C6A04A]">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-[#F7F4ED]/25 px-4 py-2 text-xs font-semibold hover:border-[#C6A04A]">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="container-page py-10">
        <div className="mb-6 flex gap-2">
          <button
            onClick={() => setTab("family")}
            data-testid="admin-tab-family"
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === "family" ? "bg-[#0B0A08] text-[#F7F4ED]" : "border border-[#0B0A08]/15 bg-white text-[#0B0A08]/70"
            }`}
          >
            Family requests ({data?.counts?.family_requests ?? 0})
          </button>
          <button
            onClick={() => setTab("nanny")}
            data-testid="admin-tab-nanny"
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === "nanny" ? "bg-[#0B0A08] text-[#F7F4ED]" : "border border-[#0B0A08]/15 bg-white text-[#0B0A08]/70"
            }`}
          >
            Nanny applications ({data?.counts?.nanny_applications ?? 0})
          </button>
        </div>

        {list.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#0B0A08]/20 bg-white p-12 text-center text-sm text-[#0B0A08]/50" data-testid="admin-empty">
            No submissions yet.
          </div>
        ) : (
          <div className="space-y-3">
            {list.map((item) => (
              <Card key={item.id} item={item} token={token} kind={tab} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
